import type { DiceResolvableSourceCustomData, PlaybackUrlData, ResolveStreamInfoResponse, StreamData } from '@dicetechnology/dice-doris';
import {
    EndpointVersions,
    URL_EXPIRY,
    getMediaCapabilities,
    resolveStreamInfo,
    resolveStreamUsingMediaCapabilities
} from '@dicetechnology/dice-doris';
import { Lazy } from '@dicetechnology/doris-core';

import { DiceReceiverManager } from '../services';

import { getMediaCapabilitiesSourceResolverConfig } from './getMediaCapabilitiesSourceResolverConfig';
import { preferDashStreamsInMediaCapabilities } from './preferDashStreamsInMediaCapabilities';

const mediaCapabilitiesPromise = new Lazy(() => {
    const config = getMediaCapabilitiesSourceResolverConfig();
    const mediaCapabilitiesPromise = getMediaCapabilities({
        mediaCapabilitiesOptions: {
            enableCbcs: config?.encryptionSchemes?.enableCbcs
        }
    });
    return mediaCapabilitiesPromise;
});

const mediaCapabilitiesWithDashPreferredPromise = new Lazy(() => {
    return mediaCapabilitiesPromise.value.then(preferDashStreamsInMediaCapabilities);
});

export const prefetchDiceStream = async (
    id: string,
    customData: DiceResolvableSourceCustomData
): Promise<StreamData | ResolveStreamInfoResponse<PlaybackUrlData> | ResolveStreamInfoResponse<PlaybackUrlData[]>> => {
    const diceReceiverManager = DiceReceiverManager.getInstance();
    const httpService = diceReceiverManager.getHttpService();
    const withV5 = diceReceiverManager.getWillUseV5Endpoint(customData.isLive);
    const googlePalManager = diceReceiverManager.getGooglePalManager();
    const advertising = {
        adProviders: googlePalManager?.getAdProviders(),
        googlePalService: googlePalManager?.getService()
    };

    if (withV5) {
        customData.experimentalEndpointVersion = EndpointVersions.V5;

        const mediaCapabilities = diceReceiverManager.getSettings()?.preferDash
            ? await mediaCapabilitiesWithDashPreferredPromise.value
            : await mediaCapabilitiesPromise.value;

        const streamData = await resolveStreamUsingMediaCapabilities({
            advertising,
            customData,
            httpService,
            id,
            mediaCapabilities
        });

        customData.resolvedData = {
            streamInfo: {
                value: streamData,
                expiry: new Date(Date.now() + URL_EXPIRY)
            }
        };

        return streamData;
    } else {
        const streamInfo = await resolveStreamInfo({
            advertising,
            customData,
            httpService,
            id,
            includeMetadata: true
        });

        customData.resolvedData = {
            streamInfo: {
                value: streamInfo,
                expiry: new Date(Date.now() + URL_EXPIRY)
            }
        };

        return streamInfo;
    }
};
